import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import DangerZone from "./DangerZone";
import { UserPlus, Users, Trash2, Save, Loader2, Target, Stethoscope, FileHeart, Upload, FileDown, FileText, TrendingUp, CheckCircle2, BellRing, CalendarClock } from "lucide-react";

const BLANK = { name: "", member_id: "", diagnosis: "", pcp_summary: "", goals: "", interventions: "", review_date: "" };

// arrays <-> newline text
const toText = (arr) => (Array.isArray(arr) ? arr.join("\n") : arr || "");
const toArr = (txt) => txt.split("\n").map((s) => s.trim()).filter(Boolean);

function reviewStatus(consumer) {
  const d = consumer.review_date || consumer.pcp?.review_date;
  if (!d) return null;
  const date = new Date(`${d}T00:00:00`);
  if (isNaN(date)) return null;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const days = Math.round((date - startOfToday) / 86400000);
  if (days < 0) return { level: "overdue", days, label: "PCP review overdue" };
  if (days <= 30) return { level: "soon", days, label: `PCP review in ${days}d` };
  return { level: "ok", days, label: `Review ${d}` };
}

export default function ConsumerManager() {
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/consumers");
      setConsumers(data);
    } catch {
      toast.error("Could not load consumers.");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/consumers/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.created} of ${data.total_rows} rows.`);
      if (data.errors?.length) toast.info(`${data.errors.length} row(s) skipped.`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv =
      "name,member_id,diagnosis,pcp_summary,goals,interventions\n" +
      'James Carter,MCD-100238,Major Depressive Disorder,Focus on independent living,"Manage finances independently;Develop coping skills","Budgeting skills training;Coping-skills coaching"\n';
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "consumers-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportClients = async () => {
    try {
      const res = await api.get("/consumers/export/simplepractice", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "simplepractice-clients.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Client roster exported for SimplePractice.");
    } catch {
      toast.error("Could not export client roster.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const [progress, setProgress] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);

  const loadProgress = async (id) => {
    setProgressLoading(true);
    setProgress([]);
    try {
      const { data } = await api.get(`/consumers/${id}/notes`);
      setProgress(data);
    } catch {
      /* ignore */
    } finally {
      setProgressLoading(false);
    }
  };

  const startNew = () => {
    setForm(BLANK);
    setEditingId(null);
    setProgress([]);
  };

  const startEdit = (c) => {
    setForm({
      name: c.name || "",
      member_id: c.member_id || "",
      diagnosis: c.diagnosis || "",
      pcp_summary: c.pcp_summary || "",
      goals: toText(c.goals),
      interventions: toText(c.interventions),
      review_date: c.review_date || c.pcp?.review_date || "",
    });
    setEditingId(c.id);
    loadProgress(c.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const downloadPcpPdf = async (c, kind = "pdf") => {
    const pcp = c.pcp || {
      member_name: c.name, member_id: c.member_id, diagnosis: c.diagnosis,
      summary: c.pcp_summary, goals: c.goals || [], interventions: c.interventions || [],
    };
    const isDocx = kind === "docx";
    try {
      const res = await api.post(`/pcp/${kind}`, { pcp }, { responseType: "blob" });
      const type = isDocx ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "application/pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PCP-${(c.name || "member").replace(/\s+/g, "_")}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(isDocx ? "NC PCP Word form downloaded!" : "PCP PDF downloaded!");
    } catch {
      toast.error(`Could not build PCP ${isDocx ? "Word file" : "PDF"}.`);
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.info("Consumer name is required.");
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name,
      member_id: form.member_id,
      diagnosis: form.diagnosis,
      pcp_summary: form.pcp_summary,
      goals: toArr(form.goals),
      interventions: toArr(form.interventions),
      review_date: form.review_date,
    };
    try {
      if (editingId) {
        await api.put(`/consumers/${editingId}`, payload);
        toast.success("Consumer updated.");
      } else {
        await api.post("/consumers", payload);
        toast.success("Consumer added.");
      }
      startNew();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/consumers/${id}`);
      if (id === editingId) startNew();
      await load();
      toast.success("Consumer removed.");
    } catch {
      toast.error("Could not remove consumer.");
    }
  };

  return (
    <div data-testid="consumer-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
            <FileHeart className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">
              Consumers & PCPs
            </h1>
            <p className="text-sm text-[#7A8C82]">
              Add each consumer's Person-Centered Plan, goals, and interventions. Staff notes draw from these automatically.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="export-clients-btn"
            onClick={exportClients}
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-[#E2DFD8] text-sm font-medium text-[#4A6B53] hover:border-[#4A6B53] transition-colors"
          >
            <Users className="w-4 h-4" strokeWidth={1.5} /> Export clients (SP)
          </button>
          <button
            data-testid="download-template-btn"
            onClick={downloadTemplate}
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-[#E2DFD8] text-sm font-medium text-[#3A453F] hover:border-[#4A6B53] transition-colors"
          >
            <FileDown className="w-4 h-4" strokeWidth={1.5} /> Template
          </button>
          <label
            data-testid="import-consumers-btn"
            className="flex items-center gap-2 h-10 px-4 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-sm font-medium cursor-pointer transition-colors shadow-[0_6px_14px_rgba(74,107,83,0.2)]"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
            Import spreadsheet
            <input
              data-testid="import-file-input"
              type="file"
              accept=".csv,.xlsx,.xlsm"
              className="hidden"
              onChange={handleImport}
              disabled={importing}
            />
          </label>
        </div>
      </div>

      {/* PCP renewal reminders */}
      {(() => {
        const due = consumers.map((c) => ({ c, r: reviewStatus(c) })).filter((x) => x.r && x.r.level !== "ok");
        if (!due.length) return null;
        return (
          <div data-testid="renewal-banner" className="mb-6 rounded-xl border border-[#F0D9D2] bg-[#FBF3F0] p-4">
            <p className="text-[10px] font-bold text-[#C26D5C] uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <BellRing className="w-3.5 h-3.5" strokeWidth={1.5} /> PCP renewals need attention ({due.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {due.map(({ c, r }) => (
                <button
                  key={c.id}
                  data-testid={`renewal-chip-${c.id}`}
                  onClick={() => startEdit(c)}
                  className={`text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors ${
                    r.level === "overdue" ? "bg-[#C26D5C] border-[#a9553f] text-white" : "bg-white border-[#F0D9D2] text-[#a9553f] hover:border-[#C26D5C]"
                  }`}
                >
                  {c.name} — {r.label}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="grid grid-cols-12 gap-6">
        {/* form */}
        <div className="col-span-12 lg:col-span-7 order-2 lg:order-1">
          <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-lg font-bold text-[#121814]">
                {editingId ? "Edit consumer" : "Add a consumer"}
              </h3>
              {editingId && (
                <div className="flex items-center gap-3">
                  <button
                    data-testid="consumer-pcp-docx-btn"
                    onClick={() => downloadPcpPdf(consumers.find((c) => c.id === editingId) || {}, "docx")}
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#4A6B53] hover:underline"
                  >
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> NC PCP Form (Word)
                  </button>
                  <button
                    data-testid="consumer-pcp-pdf-btn"
                    onClick={() => downloadPcpPdf(consumers.find((c) => c.id === editingId) || {})}
                    className="flex items-center gap-1.5 text-xs font-semibold text-[#4A6B53] hover:underline"
                  >
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> PCP PDF
                  </button>
                  <button
                    data-testid="consumer-cancel-edit"
                    onClick={startNew}
                    className="text-xs font-semibold text-[#7A8C82] hover:text-[#4A6B53]"
                  >
                    + New instead
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Full Name</Label>
                <Input data-testid="consumer-name-input" value={form.name} onChange={set("name")} placeholder="James Carter" className="h-11 bg-[#F9F9F6] border-[#E2DFD8]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Member ID / Record #</Label>
                <Input data-testid="consumer-memberid-input" value={form.member_id} onChange={set("member_id")} placeholder="MCD-100238" className="h-11 bg-[#F9F9F6] border-[#E2DFD8]" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Diagnosis</Label>
                <Input data-testid="consumer-diagnosis-input" value={form.diagnosis} onChange={set("diagnosis")} placeholder="e.g. Schizophrenia, MDD" className="h-11 bg-[#F9F9F6] border-[#E2DFD8]" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.5} /> PCP Review Date
                </Label>
                <Input data-testid="consumer-review-date-input" type="date" value={form.review_date} onChange={set("review_date")} className="h-11 bg-[#F9F9F6] border-[#E2DFD8]" />
              </div>
            </div>

            <div className="space-y-1.5 mt-4">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider flex items-center gap-1.5">
                <Stethoscope className="w-3.5 h-3.5" strokeWidth={1.5} /> PCP Summary
              </Label>
              <Textarea data-testid="consumer-pcp-input" value={form.pcp_summary} onChange={set("pcp_summary")} rows={3} placeholder="Brief overview of the Person-Centered Plan…" className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
            </div>

            <div className="space-y-1.5 mt-4">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5" strokeWidth={1.5} /> Goals — one per line
              </Label>
              <Textarea data-testid="consumer-goals-input" value={form.goals} onChange={set("goals")} rows={4} placeholder={"Manage personal finances independently\nDevelop healthy coping skills for anxiety\nMaintain stable housing"} className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
            </div>

            <div className="space-y-1.5 mt-4">
              <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider flex items-center gap-1.5">
                <Stethoscope className="w-3.5 h-3.5" strokeWidth={1.5} /> Approved Interventions — one per line
              </Label>
              <Textarea data-testid="consumer-interventions-input" value={form.interventions} onChange={set("interventions")} rows={4} placeholder={"Budgeting & money-management skills training\nCoping-skills coaching (deep breathing, grounding)\nCommunity resource navigation"} className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
            </div>

            <Button
              data-testid="consumer-save-btn"
              onClick={save}
              disabled={saving}
              className="mt-5 h-11 px-6 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  {editingId ? <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> : <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />}
                  {editingId ? "Save Changes" : "Add Consumer"}
                </>
              )}
            </Button>
          </div>

          {/* progress snapshot */}
          {editingId && (
            <div className="mt-6 bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-6" data-testid="progress-snapshot">
              <p className="text-[10px] font-bold text-[#4A6B53] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.5} /> Goal Progress Over Time
              </p>
              {progressLoading && <p className="text-sm text-[#7A8C82]">Loading progress…</p>}
              {!progressLoading && progress.length === 0 && (
                <p className="text-sm text-[#7A8C82]">No saved notes for this consumer yet. Progress will appear here as staff write notes.</p>
              )}
              {progress.length > 0 && (
                <div className="relative pl-5">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#E2DFD8]" />
                  <div className="space-y-4">
                    {progress.map((p) => (
                      <div key={p.id} className="relative" data-testid={`progress-item-${p.id}`}>
                        <span className={`absolute -left-[13px] top-1 w-2.5 h-2.5 rounded-full ${p.status === "reviewed" ? "bg-[#4A6B53]" : "bg-[#D4A373]"}`} />
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-bold text-[#121814]">{p.date_of_service || new Date(p.created_at).toLocaleDateString()}</p>
                          {p.status === "reviewed" && (
                            <span className="flex items-center gap-1 text-[9px] font-bold uppercase text-[#4A6B53]"><CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Reviewed</span>
                          )}
                        </div>
                        {p.goals_addressed && <p className="text-xs text-[#7A8C82] mt-0.5"><span className="font-semibold text-[#3A453F]">Goal:</span> {p.goals_addressed}</p>}
                        {p.progress && <p className="text-sm text-[#3A453F] mt-0.5 leading-relaxed">{p.progress}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* list */}
        <div className="col-span-12 lg:col-span-5 order-1 lg:order-2">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Users className="w-4 h-4 text-[#7A8C82]" strokeWidth={1.5} />
            <span className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-[0.2em]">
              {consumers.length} Consumer{consumers.length === 1 ? "" : "s"}
            </span>
          </div>
          {loading && <p className="text-sm text-[#7A8C82] px-1">Loading…</p>}
          {!loading && consumers.length === 0 && (
            <p className="text-sm text-[#7A8C82] px-1">No consumers yet. Add one on the left.</p>
          )}
          <div className="space-y-2">
            {consumers.map((c) => (
              <div
                key={c.id}
                data-testid={`consumer-item-${c.id}`}
                className={`group p-4 rounded-xl border transition-colors cursor-pointer ${
                  editingId === c.id ? "bg-[#DCE5DF] border-[#4A6B53]" : "bg-white border-[#E2DFD8] hover:bg-[#F4F3EF]"
                }`}
                onClick={() => startEdit(c)}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#121814] truncate">{c.name}</p>
                    <p className="text-xs text-[#7A8C82] truncate">
                      {c.member_id || "No record #"}{c.diagnosis ? ` · ${c.diagnosis}` : ""}
                    </p>
                  </div>
                  <span
                    role="button"
                    data-testid={`consumer-delete-${c.id}`}
                    onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                    className="opacity-0 group-hover:opacity-100 text-[#7A8C82] hover:text-[#C26D5C] transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-[#EAE8E3] text-[#4A6B53]">
                    {(c.goals || []).length} goal{(c.goals || []).length === 1 ? "" : "s"}
                  </span>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-[#EAE8E3] text-[#4A6B53]">
                    {(c.interventions || []).length} intervention{(c.interventions || []).length === 1 ? "" : "s"}
                  </span>
                  {(() => {
                    const r = reviewStatus(c);
                    if (!r || r.level === "ok") return null;
                    return (
                      <span data-testid={`renewal-badge-${c.id}`} className={`text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 ${r.level === "overdue" ? "bg-[#C26D5C] text-white" : "bg-[#F6E9C9] text-[#8a6d1f]"}`}>
                        <BellRing className="w-3 h-3" strokeWidth={2} /> {r.label}
                      </span>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <DangerZone onReset={load} />
    </div>
  );
}
